import * as React from 'react';
import { Subtract } from 'utility-types';
import { IProject, Map } from '../../@types/models/Project';
import { LoadingState } from '../LoadingState';
import { Infos } from '../../@types/models/Infos';

export type ApiContextProps = {
    loading: LoadingState;
    maps: Map[];
    getProject: (id: string) => Promise<IProject>;
    infos: Infos;
};

const initState: ApiContextProps = {
    loading: LoadingState.init,
    maps: [],
    getProject: (id: string) => ({}) as never,
    infos: [] as never
};

export const ApiContext = React.createContext<ApiContextProps>(initState);

export class ApiProvider extends React.Component<{}, ApiContextProps> {
    private static cache: { [uid: string]: IProject } = {};

    static Inject = <P extends ApiContextProps>(
        UnwrappedComponent: React.ComponentType<P>
    ) =>
        class WithScreen extends React.Component<
            Subtract<P, ApiContextProps>
            > {
            render() {
                return (
                    <ApiContext.Consumer>
                        {(context) => {
                            return <UnwrappedComponent {...this.props} {...context} />;
                        }
                        }
                    </ApiContext.Consumer>
                );
            }
        }

    static Consumer: React.SFC<{ children: (context: ApiContextProps) => React.ReactNode }> = (props) => {
        return <ApiContext.Consumer>{props.children}</ApiContext.Consumer>;
    }

    constructor(props: {}) {
        super(props);

        this.state = {
            ...initState, 
            getProject: this._getProject.bind(this)
        };
    }

    componentDidMount() {
        this._updateMaps();
        this._updateInfos();
    }
    render() {
        return (
            <ApiContext.Provider value={this.state}>
                {this.props.children}
            </ApiContext.Provider>
        );
    }

    private _getProject(uid: string): Promise<IProject> {
        const cached = ApiProvider.cache[uid];
        if (cached) {
            return Promise.resolve(cached);
        }
        return this._fetch(uid);
    }

    private _fetch(id: string): Promise<IProject> {
        const requestOptions = {
            headers: { 'Content-Type': 'application/json' },
            method: 'GET'
        };

        return fetch(`/api/projects/${id}`, requestOptions)
            .then(response => {

                return response
                    .json()
                    .then(json => {
                        ApiProvider.cache[id] = json;
                        return json;
                    });

            });
    }

    private _updateMaps() {
        const requestOptions = {
            headers: { 'Content-Type': 'application/json' },
            method: 'GET'
        };

        this.setState(prev => ({
            loading: LoadingState.loading
        }));

        fetch(`/api/projects/maps`, requestOptions)
            .then(response => {
                if (!response) {
                    this.setState(prev => ({
                        loading: LoadingState.error
                    }));
                    return;
                }
                response
                    .json()
                    .then(json => {
                        this.setState(prev => ({
                            loading: LoadingState.done,
                            maps: json || []
                        }));

                        return response;
                    });

            }).catch(() => {
                this.setState(prev => ({
                    loading: LoadingState.error
                }));
            });
    }

    private _updateInfos() {
        const requestOptions = {
            headers: { 'Content-Type': 'application/json' },
            method: 'GET'
        };

        this.setState(prev => ({
            loading: LoadingState.loading
        }));

        fetch(`/api/infos`, requestOptions)
            .then(response => {
                if (!response) {
                    this.setState(prev => ({
                        loading: LoadingState.error
                    }));
                    return;
                }
                response
                    .json()
                    .then(json => {
                        this.setState(prev => ({
                            loading: LoadingState.done,
                            infos: json || []
                        }));

                        return response;
                    });

            }).catch(() => {
                this.setState(prev => ({
                    loading: LoadingState.error
                }));
            });
    }
}

export default ApiProvider;


// WEBPACK FOOTER //
// ./src/_context/Api.tsx